import { useEffect, useRef, useState } from 'react';
import useSWRInfinite from 'swr/infinite';
import { ImageOff, Loader2 } from 'lucide-react';
import { usePageTitle } from '@/contexts/PageHeaderContext';
import { useCurrentZone } from '@/hooks/useCurrentZone';
import { BranchRequiredState } from '@/components/common/BranchRequiredState';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { PolygonCrop } from '@/components/projection/PolygonCrop';
import { toAbsoluteUrl } from '@/api/url';
import { API_HOST } from '@/api/base';
import { getPredictionsFeed, type FeedItem, type FeedPage } from '@/api/predictionsFeed';
import { cn } from '@/lib/utils';

// Лента предсказаний (Алексей 23.10 #2137): по филиалу из обычного селектора — чтение за
// чтением, новые сверху, бесконечная прокрутка вниз; первая страница обновляется сама раз в
// 15 с. Чтобы видеть флаки модели: кадр ровно этого чтения (пока он есть в буфере, ~5 мин),
// вердикт и скоры.

const RACK_LABEL: Record<string, string> = {
  FULL: 'полная', TIDY_BUT_LOW_STOCK: 'аккуратно, мало', PARTIALLY_EMPTY: 'частично',
  NOTICEABLY_EMPTY: 'заметно пустеет', ALMOST_EMPTY: 'почти пустая',
};
const RACK_TONE: Record<string, string> = {
  FULL: 'bg-success/15 text-success', TIDY_BUT_LOW_STOCK: 'bg-yellow-300/30 text-yellow-800 dark:text-yellow-200',
  PARTIALLY_EMPTY: 'bg-amber-400/20 text-amber-700 dark:text-amber-300', NOTICEABLY_EMPTY: 'bg-orange-500/15 text-orange-700 dark:text-orange-300',
  ALMOST_EMPTY: 'bg-red-600/15 text-red-700 dark:text-red-300',
};
const TABLE_LABEL: Record<string, string> = {
  occupied: 'занят', dirty: 'грязный', shifted: 'не на месте', free: 'свободен', spoiled: 'не определить',
};
const TABLE_TONE: Record<string, string> = {
  occupied: 'bg-success/15 text-success', dirty: 'bg-orange-500/15 text-orange-700 dark:text-orange-300',
  shifted: 'bg-muted text-foreground', free: 'bg-muted text-muted-foreground', spoiled: 'bg-muted text-muted-foreground',
};
const SCORE_KEYS: [string, string][] = [['occupied', 'занят'], ['dirty', 'грязно'], ['food', 'еда'], ['personal', 'вещи'], ['in_frame', 'в кадре']];

const hms = (iso: string) => new Date(iso).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
const dm = (iso: string) => new Date(iso).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });

function Bar({ value, mark, warn }: { value: number; mark?: number; warn?: boolean }) {
  return (
    <span className="relative h-1.5 w-16 shrink-0 overflow-hidden rounded-full bg-muted">
      <span className={cn('absolute inset-y-0 left-0', warn ? 'bg-amber-500' : 'bg-primary/60')} style={{ width: `${Math.max(0, Math.min(1, value)) * 100}%` }} />
      {mark != null && <span className="absolute inset-y-0 w-px bg-foreground" style={{ left: `${Math.min(1, mark) * 100}%` }} />}
    </span>
  );
}

function FeedCard({ it }: { it: FeedItem }) {
  const src = it.image_url ? toAbsoluteUrl(it.image_url, API_HOST) : null;
  const [failed, setFailed] = useState(false);
  const label = it.kind === 'layout' ? RACK_LABEL[it.verdict ?? ''] ?? it.verdict ?? '—' : TABLE_LABEL[it.verdict ?? ''] ?? it.verdict ?? '—';
  const tone = it.kind === 'layout' ? RACK_TONE[it.verdict ?? ''] : TABLE_TONE[it.verdict ?? ''];
  return (
    <div className="overflow-hidden rounded-lg border border-border bg-card">
      <a href={src ?? undefined} target="_blank" rel="noreferrer" className="relative block aspect-video bg-muted">
        {src && !failed ? (
          it.image_kind === 'frame' && it.polygons?.length
            // Кроп по контуру — объект крупно; полный кадр — по клику (ссылка).
            ? <PolygonCrop frameSrc={src} polygons={it.polygons} alt={it.name} className="absolute inset-0 size-full object-contain" onUnavailable={() => setFailed(true)} />
            : <img src={src} alt={it.name} className="absolute inset-0 size-full object-contain" onError={() => setFailed(true)} />
        ) : (
          <span className="absolute inset-0 flex flex-col items-center justify-center gap-1 text-[11px] text-muted-foreground">
            <ImageOff className="size-5" />кадр не сохранился
          </span>
        )}
      </a>
      <div className="space-y-1.5 px-3 py-2">
        <div className="flex items-center gap-2 text-xs">
          <span className="tabular-nums text-muted-foreground">{hms(it.at)}</span>
          <span className="min-w-0 flex-1 truncate font-medium">{it.name}</span>
          <span className={cn('shrink-0 rounded px-1.5 py-0.5 text-[11px] font-medium', tone ?? 'bg-muted')}>{label}</span>
        </div>
        {it.kind === 'layout' && (
          <div className="space-y-0.5">
            {(it.shelves ?? []).map((s) => (
              <div key={s.name} className="flex items-center gap-2 text-[11px] text-muted-foreground">
                <span className="min-w-0 flex-1 truncate">{s.name}</span>
                <span className="tabular-nums">{Math.round(s.pct * 100)}%</span>
                <Bar value={s.pct} mark={s.thr} warn={s.pct < s.thr} />
              </div>
            ))}
          </div>
        )}
        {it.kind === 'tables' && it.scores && (
          <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
            {SCORE_KEYS.filter(([k]) => it.scores?.[k] != null).map(([k, l]) => (
              <div key={k} className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                <span className="w-12 shrink-0">{l}</span>
                <Bar value={it.scores![k]} />
                <span className="tabular-nums">{it.scores![k].toFixed(2)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function PredictionsFeedPage() {
  usePageTitle('Лента предсказаний', 'Последние чтения моделей по филиалу: кадр, вердикт, скоры');
  const { zoneId } = useCurrentZone();
  const [kinds, setKinds] = useState('layout,tables');
  const { data, size, setSize, isValidating, mutate } = useSWRInfinite<FeedPage>(
    (i, prev: FeedPage | null) => {
      if (!zoneId) return null;
      if (i === 0) return ['feed', zoneId, kinds, null];
      if (!prev?.next) return null;
      return ['feed', zoneId, kinds, prev.next];
    },
    ([, z, k, before]) => getPredictionsFeed(z as string, k as string, before as string | null).then((r) => r.data),
    { revalidateFirstPage: true, revalidateOnFocus: false, refreshInterval: 15_000 },
  );
  const items = (data ?? []).flatMap((p) => p.items);
  const seen = new Set<string>();
  const uniq = items.filter((i) => (seen.has(i.id) ? false : (seen.add(i.id), true)));
  const end = !!data && !data[data.length - 1]?.next;
  const sentinel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = sentinel.current;
    if (!el) return;
    const io = new IntersectionObserver((es) => { if (es[0].isIntersecting && !isValidating && !end) void setSize(size + 1); }, { rootMargin: '600px' });
    io.observe(el);
    return () => io.disconnect();
  }, [size, isValidating, end, setSize]);
  useEffect(() => { void mutate(); }, [kinds]);

  if (!zoneId) return <BranchRequiredState />;
  // Разделители по дням — лента бывает длинной.
  let lastDay = '';
  return (
    <div className="space-y-3 p-4 md:p-6">
      <div className="flex flex-wrap items-center gap-3">
        <ToggleGroup value={kinds} onChange={(v) => v && setKinds(v)}>
          <ToggleGroupItem value="layout,tables">Всё</ToggleGroupItem>
          <ToggleGroupItem value="layout">Выкладка</ToggleGroupItem>
          <ToggleGroupItem value="tables">Столы</ToggleGroupItem>
        </ToggleGroup>
        <span className="text-xs text-muted-foreground">Кадр чтения хранится ~5 мин, пока страницу смотрят; у старых чтений — сохранённый кадр или кроп, если был.</span>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
        {uniq.map((it) => {
          const day = it.at.slice(0, 10);
          const sep = day !== lastDay && lastDay !== '';
          lastDay = day;
          return (
            <div key={it.id} className="contents">
              {sep && <div className="col-span-full pt-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">{dm(it.at)}</div>}
              <FeedCard it={it} />
            </div>
          );
        })}
      </div>
      <div ref={sentinel} className="flex h-12 items-center justify-center text-xs text-muted-foreground">
        {isValidating ? <Loader2 className="size-4 animate-spin" /> : end ? (uniq.length ? 'Дальше чтений нет (лента — до 7 дней).' : 'Чтений нет.') : null}
      </div>
    </div>
  );
}
